import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, App, Space, Select, Tag, Switch,
  DatePicker, Image, InputNumber, Card, Row, Col
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import request from '../utils/request';
import { getCurrentUser } from '../utils/user';

const TYPE_OPTIONS = [
  { value: 'announcement', label: '公告', color: 'blue' },
  { value: 'ad', label: '广告', color: 'orange' }
];

const Announcement = () => {
  const { message } = App.useApp();
  const currentUser = getCurrentUser();
  const isSuperAdmin = Boolean(currentUser.isSuperAdmin);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [form] = Form.useForm();
  const [editingRecord, setEditingRecord] = useState(null);
  const [communities, setCommunities] = useState([]);

  const [filter, setFilter] = useState({ title: '', type: '', status: '' });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  useEffect(() => { fetchCommunities(); }, []);
  useEffect(() => { fetchData(); }, []);

  const fetchCommunities = async () => {
    try {
      const res = await request.get('/community', { params: { page: 1, pageSize: 999 } });
      if (res.code === 200) setCommunities(res.data.list || []);
    } catch (e) { console.error('fetchCommunities failed', e); }
  };

  const fetchData = async (page = pagination.current, pageSize = pagination.pageSize) => {
    setLoading(true);
    try {
      const params = { page, pageSize };
      if (filter.title.trim()) params.title = filter.title.trim();
      if (filter.type) params.type = filter.type;
      if (filter.status !== '' && filter.status !== null && filter.status !== undefined) {
        params.status = filter.status;
      }
      const res = await request.get('/announcement/list', { params });
      if (res.code === 200) {
        setData(res.data.list || []);
        setPagination({ current: res.data.page || page, pageSize: res.data.pageSize || pageSize, total: res.data.total || 0 });
      } else {
        message.error(res.message || '获取失败');
      }
    } catch (e) {
      message.error('获取公告列表失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => { fetchData(1, pagination.pageSize); };
  const handleResetFilter = () => {
    setFilter({ title: '', type: '', status: '' });
    setTimeout(() => fetchData(1, pagination.pageSize), 0);
  };

  const handleAdd = () => {
    setEditingRecord(null);
    form.resetFields();
    form.setFieldsValue({
      type: 'announcement',
      status: 1,
      sortOrder: 0,
      communityId: !isSuperAdmin ? currentUser.communityId : null
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    const values = { ...record };
    if (values.startTime) values.startTime = dayjs(values.startTime);
    if (values.endTime) values.endTime = dayjs(values.endTime);
    form.setFieldsValue(values);
    setModalVisible(true);
  };

  const handlePreview = (record) => {
    setPreviewItem(record);
    setPreviewVisible(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除这条公告吗?此操作不可恢复。',
      okText: '确认',
      cancelText: '取消',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await request.delete('/announcement/' + id);
          message.success('删除成功');
          fetchData();
        } catch (e) {
          message.error('删除失败');
        }
      }
    });
  };

  const handleToggleStatus = async (record, checked) => {
    try {
      await request.put('/announcement/' + record.id, { status: checked ? 1 : 0 });
      message.success(checked ? '已启用' : '已禁用');
      fetchData();
    } catch (e) {
      message.error('操作失败');
    }
  };

  const handleSubmit = async (values) => {
    try {
      const payload = { ...values };
      if (payload.startTime) payload.startTime = payload.startTime.format('YYYY-MM-DD HH:mm:ss');
      if (payload.endTime) payload.endTime = payload.endTime.format('YYYY-MM-DD HH:mm:ss');
      if (!isSuperAdmin) {
        payload.communityId = currentUser.communityId;
      }
      if (editingRecord) {
        await request.put('/announcement/' + editingRecord.id, payload);
        message.success('更新成功');
      } else {
        await request.post('/announcement', payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      fetchData();
    } catch (e) {
      message.error(editingRecord ? '更新失败' : '创建失败');
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 60 },
    {
      title: '类型', dataIndex: 'type', key: 'type', width: 90,
      render: (t) => {
        const o = TYPE_OPTIONS.find(x => x.value === t) || { label: t, color: 'default' };
        return <Tag color={o.color}>{o.label}</Tag>;
      }
    },
    { title: '标题', dataIndex: 'title', key: 'title', ellipsis: true },
    {
      title: '封面', dataIndex: 'imageUrl', key: 'imageUrl', width: 80,
      render: (url) => url ? <Image src={url} width={48} height={48} style={{ objectFit: 'cover', borderRadius: 4 }} /> : <span style={{ color: '#ccc' }}>-</span>
    },
    { title: '所属小区', dataIndex: 'communityName', key: 'communityName', width: 140, render: (n) => n || <Tag color="purple">全部小区</Tag> },
    {
      title: '状态', dataIndex: 'status', key: 'status', width: 100,
      render: (s, record) => (
        <Switch
          checked={s === 1}
          onChange={(checked) => handleToggleStatus(record, checked)}
          checkedChildren="启用"
          unCheckedChildren="禁用"
        />
      )
    },
    { title: '排序', dataIndex: 'sortOrder', key: 'sortOrder', width: 80 },
    {
      title: '生效时间', key: 'time', width: 240,
      render: (_, r) => (
        <span style={{ fontSize: 12, color: '#666' }}>
          {r.startTime || '不限'} ~ {r.endTime || '不限'}
        </span>
      )
    },
    { title: '创建人', dataIndex: 'createdByName', key: 'createdByName', width: 100 },
    { title: '创建时间', dataIndex: 'createdAt', key: 'createdAt', width: 160 },
    {
      title: '操作', key: 'action', width: 240, fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handlePreview(record)}>预览</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>删除</Button>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col>
            <span style={{ marginRight: 8 }}>标题:</span>
            <Input
              placeholder="搜索标题"
              value={filter.title}
              onChange={e => setFilter({ ...filter, title: e.target.value })}
              onPressEnter={handleSearch}
              style={{ width: 180 }}
              allowClear
            />
          </Col>
          <Col>
            <span style={{ marginRight: 8 }}>类型:</span>
            <Select
              value={filter.type}
              onChange={v => setFilter({ ...filter, type: v })}
              style={{ width: 120 }}
              allowClear
              placeholder="全部"
            >
              {TYPE_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
            </Select>
          </Col>
          <Col>
            <span style={{ marginRight: 8 }}>状态:</span>
            <Select
              value={filter.status}
              onChange={v => setFilter({ ...filter, status: v })}
              style={{ width: 120 }}
              allowClear
              placeholder="全部"
            >
              <Select.Option value={1}>启用</Select.Option>
              <Select.Option value={0}>禁用</Select.Option>
            </Select>
          </Col>
          <Col>
            <Space>
              <Button type="primary" onClick={handleSearch}>查询</Button>
              <Button icon={<ReloadOutlined />} onClick={handleResetFilter}>重置</Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>发布公告</Button>
      </div>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (page, pageSize) => fetchData(page, pageSize)
        }}
      />

      <Modal
        title={editingRecord ? '编辑公告' : '发布公告'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={720}
        destroyOnClose
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="类型" name="type" rules={[{ required: true, message: '请选择类型' }]}>
                <Select placeholder="请选择类型">
                  {TYPE_OPTIONS.map(o => <Select.Option key={o.value} value={o.value}>{o.label}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="所属小区" name="communityId">
                <Select
                  placeholder={isSuperAdmin ? '不选则对所有小区可见' : '当前管理员小区'}
                  allowClear
                  disabled={!isSuperAdmin}
                >
                  {communities.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="标题" name="title" rules={[{ required: true, message: '请输入标题' }, { max: 200, message: '不超过 200 字' }]}>
            <Input placeholder="请输入公告标题" maxLength={200} showCount />
          </Form.Item>
          <Form.Item label="封面图URL" name="imageUrl">
            <Input placeholder="可选,填写图片URL(支持小程序前端展示)" allowClear />
          </Form.Item>
          <Form.Item label="内容" name="content" rules={[{ required: true, message: '请输入内容' }]}>
            <Input.TextArea rows={6} placeholder="请输入公告内容" maxLength={5000} showCount />
          </Form.Item>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item label="排序值" name="sortOrder" tooltip="数字越大越靠前">
                <InputNumber style={{ width: '100%' }} placeholder="0" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item label="状态" name="status" valuePropName="checked" getValueFromEvent={(v) => v ? 1 : 0} getValueProps={(v) => ({ checked: Number(v) === 1 })}>
                <Switch checkedChildren="启用" unCheckedChildren="禁用" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="生效开始时间" name="startTime">
                <DatePicker showTime style={{ width: '100%' }} placeholder="不限" format="YYYY-MM-DD HH:mm:ss" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="生效结束时间" name="endTime">
                <DatePicker showTime style={{ width: '100%' }} placeholder="不限" format="YYYY-MM-DD HH:mm:ss" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item style={{ textAlign: 'right', marginBottom: 0 }}>
            <Space>
              <Button type="primary" htmlType="submit">提交</Button>
              <Button onClick={() => setModalVisible(false)}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="公告预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={<Button onClick={() => setPreviewVisible(false)}>关闭</Button>}
        width={640}
      >
        {previewItem && (
          <div>
            <div style={{ marginBottom: 12 }}>
              {previewItem.type === 'announcement' && <Tag color="blue">公告</Tag>}
              {previewItem.type === 'ad' && <Tag color="orange">广告</Tag>}
              <span style={{ marginLeft: 8, color: '#999', fontSize: 12 }}>
                {previewItem.communityName ? `小区:${previewItem.communityName}` : '全部小区'}
              </span>
            </div>
            <h2 style={{ fontSize: 22, marginTop: 8 }}>{previewItem.title}</h2>
            <div style={{ color: '#999', fontSize: 12, marginBottom: 16 }}>
              发布:{previewItem.createdAt} 创建人:{previewItem.createdByName || '-'}
            </div>
            {previewItem.imageUrl && <Image src={previewItem.imageUrl} style={{ width: '100%', maxHeight: 320, objectFit: 'cover', borderRadius: 6, marginBottom: 16 }} />}
            <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.8, fontSize: 14 }}>{previewItem.content}</div>
            {(previewItem.startTime || previewItem.endTime) && (
              <div style={{ marginTop: 16, padding: 12, background: '#fffbe6', borderRadius: 6, fontSize: 12, color: '#666' }}>
                生效:{previewItem.startTime || '不限'} ~ {previewItem.endTime || '不限'}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Announcement;
